<template>
  <section
    ref="panel"
    class="floatingContentPanel"
    :class="{ fixedHeight: fixedHeight }"
    :style="panelStyle"
    :aria-label="title"
    @mousedown="bringToFront"
    @touchstart.passive="bringToFront"
  >
    <header
      class="floatingContentPanelHeader"
      title="Arraste para mover"
      @mousedown="startDrag"
      @touchstart="startDrag"
    >
      <div>
        <i :class="iconClass" aria-hidden="true"></i>
        <strong>{{ title }}</strong>
      </div>
      <div class="floatingContentPanelActions">
        <slot name="actions"></slot>
        <button
          type="button"
          :title="'Fechar ' + title"
          :aria-label="'Fechar ' + title"
          @click.stop="$emit('close')"
        >
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
      </div>
    </header>

    <div
      class="floatingContentPanelBody"
      :class="{ fixedHeight: fixedHeight }"
    >
      <slot></slot>
    </div>

    <button
      type="button"
      class="floatingContentPanelResize"
      :class="{ horizontal: fixedHeight }"
      :aria-label="resizeLabel"
      :title="resizeLabel"
      @mousedown="startResize"
      @touchstart="startResize"
    >
      <i
        class="fa"
        :class="fixedHeight ? 'fa-arrows-h' : 'fa-arrows-alt'"
        aria-hidden="true"
      ></i>
    </button>
  </section>
</template>

<script>
let topPanelZIndex = 1100;

export default {
  name: "FloatingContentPanel",
  props: {
    title: { type: String, required: true },
    iconClass: { type: String, default: "fa fa-window-restore" },
    storageKey: { type: String, required: true },
    initialX: { type: Number, default: 16 },
    initialY: { type: Number, default: 70 },
    initialWidth: { type: Number, default: 560 },
    initialHeight: { type: Number, default: 320 },
    fixedHeight: { type: Number, default: 0 },
    allowBottomOverflow: { type: Boolean, default: false },
    zIndex: { type: Number, default: 1060 },
  },
  data() {
    return {
      position: { x: this.initialX, y: this.initialY },
      size: { width: this.initialWidth, height: this.initialHeight },
      dragging: null,
      resizing: null,
      activeZIndex: this.zIndex,
    };
  },
  computed: {
    panelStyle() {
      return {
        left: this.position.x + "px",
        top: this.position.y + "px",
        width: this.size.width + "px",
        height: (this.fixedHeight || this.size.height) + "px",
        zIndex: this.activeZIndex,
      };
    },
    resizeLabel() {
      return this.fixedHeight
        ? "Redimensionar largura de " + this.title
        : "Redimensionar " + this.title;
    },
  },
  mounted() {
    this.loadGeometry();
    this.normalizeGeometry();
    window.addEventListener("resize", this.normalizeGeometry);
  },
  beforeDestroy() {
    this.stopDrag();
    this.stopResize();
    window.removeEventListener("resize", this.normalizeGeometry);
  },
  methods: {
    pointerFrom(event) {
      return event.touches ? event.touches[0] : event;
    },
    bringToFront() {
      topPanelZIndex = Math.max(topPanelZIndex + 1, this.zIndex + 10);
      this.activeZIndex = topPanelZIndex;
    },
    loadGeometry() {
      try {
        const saved = JSON.parse(localStorage.getItem(this.storageKey));
        if (!saved || typeof saved !== "object") {
          return;
        }

        if (Number.isFinite(saved.x) && Number.isFinite(saved.y)) {
          this.position = { x: saved.x, y: saved.y };
        }
        if (Number.isFinite(saved.width) && Number.isFinite(saved.height)) {
          this.size = { width: saved.width, height: saved.height };
        }
      } catch (error) {
        return;
      }
    },
    saveGeometry() {
      try {
        localStorage.setItem(
          this.storageKey,
          JSON.stringify({
            x: Math.round(this.position.x),
            y: Math.round(this.position.y),
            width: Math.round(this.size.width),
            height: Math.round(this.size.height),
          })
        );
      } catch (error) {
        return;
      }
    },
    normalizeGeometry() {
      if (document.fullscreenElement === this.$refs.panel) {
        return;
      }

      const margin = 10;
      const maximumWidth = Math.max(260, window.innerWidth - margin * 2);
      const maximumHeight = Math.max(150, window.innerHeight - margin * 2);
      this.size.width = Math.min(maximumWidth, Math.max(260, this.size.width));
      this.size.height = Math.min(
        maximumHeight,
        Math.max(150, this.fixedHeight || this.size.height)
      );
      if (this.fixedHeight) {
        this.size.height = this.fixedHeight;
      }
      this.position.x = Math.max(
        margin,
        Math.min(window.innerWidth - this.size.width - margin, this.position.x)
      );
      const maximumY = this.allowBottomOverflow
        ? window.innerHeight - 38
        : window.innerHeight - this.size.height - margin;
      this.position.y = Math.max(margin, Math.min(maximumY, this.position.y));
    },
    startDrag(event) {
      if (document.fullscreenElement || event.target.closest("button")) {
        return;
      }

      const pointer = this.pointerFrom(event);
      this.dragging = {
        startX: pointer.clientX,
        startY: pointer.clientY,
        x: this.position.x,
        y: this.position.y,
      };
      this.bringToFront();
      document.addEventListener("mousemove", this.onDrag);
      document.addEventListener("mouseup", this.stopDrag);
      document.addEventListener("touchmove", this.onDrag, { passive: false });
      document.addEventListener("touchend", this.stopDrag);
      event.preventDefault();
    },
    onDrag(event) {
      if (!this.dragging) {
        return;
      }

      const pointer = this.pointerFrom(event);
      this.position.x =
        this.dragging.x + pointer.clientX - this.dragging.startX;
      this.position.y =
        this.dragging.y + pointer.clientY - this.dragging.startY;
      this.normalizeGeometry();
      event.preventDefault();
    },
    stopDrag() {
      if (!this.dragging) {
        return;
      }

      this.dragging = null;
      document.removeEventListener("mousemove", this.onDrag);
      document.removeEventListener("mouseup", this.stopDrag);
      document.removeEventListener("touchmove", this.onDrag);
      document.removeEventListener("touchend", this.stopDrag);
      this.saveGeometry();
    },
    startResize(event) {
      if (document.fullscreenElement) {
        return;
      }

      const pointer = this.pointerFrom(event);
      this.resizing = {
        startX: pointer.clientX,
        startY: pointer.clientY,
        width: this.size.width,
        height: this.size.height,
        right: this.position.x + this.size.width,
        bottom: this.position.y + this.size.height,
        top: this.position.y,
      };
      this.bringToFront();
      document.addEventListener("mousemove", this.onResize);
      document.addEventListener("mouseup", this.stopResize);
      document.addEventListener("touchmove", this.onResize, { passive: false });
      document.addEventListener("touchend", this.stopResize);
      event.preventDefault();
    },
    onResize(event) {
      if (!this.resizing) {
        return;
      }

      const pointer = this.pointerFrom(event);
      const maximumWidth = Math.max(260, this.resizing.right - 10);
      const maximumHeight = Math.max(150, this.resizing.bottom - 10);
      const widthDelta = this.resizing.startX - pointer.clientX;
      this.size.width = Math.max(
        Math.min(260, maximumWidth),
        Math.min(
          maximumWidth,
          this.resizing.width + widthDelta
        )
      );
      this.position.x = this.resizing.right - this.size.width;

      if (!this.fixedHeight) {
        const heightDelta = this.resizing.startY - pointer.clientY;
        this.size.height = Math.max(
          Math.min(150, maximumHeight),
          Math.min(
            maximumHeight,
            this.resizing.height + heightDelta
          )
        );
        this.position.y = this.resizing.bottom - this.size.height;
      } else {
        this.position.y = this.resizing.top;
      }
      event.preventDefault();
    },
    stopResize() {
      if (!this.resizing) {
        return;
      }

      this.resizing = null;
      document.removeEventListener("mousemove", this.onResize);
      document.removeEventListener("mouseup", this.stopResize);
      document.removeEventListener("touchmove", this.onResize);
      document.removeEventListener("touchend", this.stopResize);
      this.saveGeometry();
    },
  },
};
</script>

<style scoped>
.floatingContentPanel {
  background: #090c0e;
  border: 1px solid #48535b;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.48);
  min-height: 150px;
  min-width: 260px;
  overflow: visible;
  position: fixed;
}

.floatingContentPanel.fixedHeight {
  min-height: 0;
}

.floatingContentPanelHeader {
  align-items: center;
  background: #1c2328;
  border-bottom: 1px solid #48535b;
  color: #f4f6f7;
  cursor: move;
  display: flex;
  height: 38px;
  justify-content: space-between;
  padding: 0 6px 0 12px;
  user-select: none;
}

.floatingContentPanelHeader > div:first-child {
  align-items: center;
  display: inline-flex;
  font-size: 12px;
  gap: 7px;
  min-width: 0;
  overflow: hidden;
}

.floatingContentPanelHeader strong {
  font-size: 12px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.floatingContentPanelActions {
  display: inline-flex;
  flex: none;
  gap: 2px;
}

.floatingContentPanelActions button,
.floatingContentPanelResize {
  align-items: center;
  background: transparent;
  border: 0;
  color: #c9d0d4;
  cursor: pointer;
  display: inline-flex;
  height: 30px;
  justify-content: center;
  padding: 0;
  width: 30px;
}

.floatingContentPanelActions button:hover,
.floatingContentPanelResize:hover {
  background: #303940;
  color: #ffffff;
}

.floatingContentPanelActions button:focus-visible,
.floatingContentPanelResize:focus-visible {
  outline: 2px solid #37b982;
  outline-offset: -2px;
}

.floatingContentPanelBody {
  background: #ffffff;
  height: calc(100% - 38px);
  overflow: auto;
  width: 100%;
}

.floatingContentPanelBody.fixedHeight {
  overflow: hidden;
}

.floatingContentPanelResize {
  background: #2d373e;
  border: 1px solid #5c6870;
  color: #f4f6f7;
  cursor: nwse-resize;
  height: 22px;
  left: -10px;
  position: absolute;
  top: -10px;
  width: 22px;
  z-index: 2;
}

.floatingContentPanelResize i {
  font-size: 10px;
  transform: rotate(90deg);
}

.floatingContentPanelResize.horizontal {
  cursor: ew-resize;
}

.floatingContentPanelResize.horizontal i {
  transform: none;
}

@media (max-width: 575px) {
  .floatingContentPanel {
    max-height: calc(100vh - 20px);
    max-width: calc(100vw - 20px);
  }
}
</style>
